import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, Mail, Rocket, Flame } from "lucide-react";

const DISMISS_KEY = "jb_sub_dismiss_until";

/**
 * Phase 12/16 subscription banner. Priority (highest first):
 *   frozen / trial_read_only  → hard blocking handled by axios redirect / page.
 *   grace                     → red, not dismissible.
 *   trial                     → blue, NOT dismissible (per Phase 16 spec).
 *   active & days_to_expiry<=7 → amber, dismissible 24h.
 */
export default function SubscriptionBanner() {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/subscription/status")
        .then(({ data }) => { if (mounted) setInfo(data); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { mounted = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    setDismissed(Date.now() < until);
  }, [info]);

  if (!info) return null;
  if (info.status === "frozen" || info.status === "trial_read_only") return null;

  const expiresLabel = info.expires_at ? new Date(info.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
  const trialExpiresLabel = info.trial_expires_at ? new Date(info.trial_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
  const dismiss = () => {
    const tomorrow = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(tomorrow));
    setDismissed(true);
  };

  // ---- Grace (red, not dismissible) ----
  if (info.status === "grace") {
    return (
      <div className="bg-rose-600 text-white border-b-2 border-rose-800" data-testid="subscription-banner-grace">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="flex-1 min-w-[260px] text-sm">
            <div className="font-bold">Your subscription expired on {expiresLabel}.</div>
            <div className="opacity-95">
              You have <span className="font-extrabold">{info.grace_days_remaining} days of grace</span> before your account is frozen.
            </div>
          </div>
          <Link to="/billing" className="inline-flex items-center gap-1.5 bg-white text-rose-700 hover:bg-rose-50 font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-md mono"
                data-testid="subscription-banner-upgrade">
            <Rocket className="h-3.5 w-3.5" /> Upgrade now
          </Link>
          {info.platform_contact_email && (
            <a href={`mailto:${info.platform_contact_email}`} className="text-white/90 hover:text-white mono text-xs">
              <Mail className="h-3.5 w-3.5 inline mr-1" />{info.platform_contact_email}
            </a>
          )}
        </div>
      </div>
    );
  }

  // Phase P.22 — Manual-subscription grace (14-day migration window after
  // super admin removes a manual plan). Renders as a persistent amber card
  // that leads directly to /billing. Not dismissible: this is the tenant's
  // one clear signal that access will end if they don't self-serve a plan.
  if (info.manual_grace_days_remaining != null) {
    const d = Number(info.manual_grace_days_remaining);
    const label = d <= 0 ? "ending today" : `${d} day${d === 1 ? "" : "s"} remaining`;
    return (
      <div className="bg-amber-500 text-white border-b-2 border-amber-700" data-testid="subscription-banner-manual-grace">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
          <Clock className="h-4 w-4 shrink-0" />
          <div className="flex-1 min-w-[240px] text-sm">
            <span className="font-bold">Set up your subscription to keep access.</span>{" "}
            <span className="opacity-95">{label}.</span>
          </div>
          <Link to="/billing" className="inline-flex items-center gap-1.5 bg-white text-amber-800 hover:bg-amber-50 font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-md mono"
                data-testid="subscription-banner-manual-grace-cta">
            <Rocket className="h-3.5 w-3.5" /> Set up subscription
          </Link>
        </div>
      </div>
    );
  }

  // ---- Trial (blue, NOT dismissible) ----
  if (info.status === "trial") {
    const n = info.trial_days_remaining;
    const daysLabel = n === null || n === undefined ? "" : (n <= 0 ? "ending today" : `${n} day${n === 1 ? "" : "s"} remaining`);
    return (
      <div className="bg-sky-600 text-white border-b-2 border-sky-800" data-testid="subscription-banner-trial">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
          <Flame className="h-4 w-4 shrink-0" />
          <div className="flex-1 min-w-[240px] text-sm">
            <span className="font-bold">Free trial · {daysLabel}.</span>{" "}
            <span className="opacity-95">Trial ends {trialExpiresLabel}. Upgrade to keep Pro features.</span>
          </div>
          <Link to="/billing" className="inline-flex items-center gap-1.5 bg-white text-sky-700 hover:bg-sky-50 font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-md mono"
                data-testid="subscription-banner-trial-upgrade">
            <Rocket className="h-3.5 w-3.5" /> See plans
          </Link>
        </div>
      </div>
    );
  }

  // ---- Active but expiring soon (amber, dismissible) ----
  if (info.status === "active" && info.days_to_expiry !== null && info.days_to_expiry <= 7) {
    if (dismissed) return null;
    return (
      <div className="bg-amber-300 text-amber-950 border-b-2 border-amber-500" data-testid="subscription-banner-warning">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
          <Clock className="h-4 w-4 shrink-0" />
          <div className="flex-1 min-w-[240px] text-sm">
            <span className="font-bold">Your subscription expires on {expiresLabel}.</span>{" "}
            <span className="opacity-90">
              ({info.days_to_expiry === 0 ? "today" : `${info.days_to_expiry} days`}) Renew from Billing to avoid interruption.
            </span>
          </div>
          <Link to="/billing" className="inline-flex items-center gap-1.5 text-amber-950 bg-white/80 hover:bg-white border border-amber-700 px-3 py-1.5 rounded-md mono text-xs uppercase tracking-wider font-bold"
                data-testid="subscription-banner-warning-upgrade">
            <Rocket className="h-3.5 w-3.5" /> Billing
          </Link>
          <Button size="sm" variant="outline" onClick={dismiss}
                  className="bg-white/60 hover:bg-white border-amber-700 text-amber-950 h-7"
                  data-testid="subscription-banner-dismiss">
            Dismiss for today
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
